// v2.19.31 — Dashboard OPS owner pour visualiser les top fiches par impressions LLM.
// ⚠️ Tracking actif différé à v2.19.32 — la page affiche la structure et un disclaimer.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "civ_user_token";

function _authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const COLLECTIONS = [
  { k: "", l: "🌐 Toutes catégories" },
  { k: "sante", l: "🏥 Santé" },
  { k: "justice", l: "⚖️ Justice" },
  { k: "commerces", l: "🛍 Commerces" },
  { k: "finance", l: "💰 Finance" },
  { k: "administration", l: "🏛 Administration" },
  { k: "shopping", l: "🎁 Shopping" },
];

export default function OpsImpressionsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [trackingActive, setTrackingActive] = useState(false);
  const [collection, setCollection] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/ops-b2b/impressions/top?limit=20${collection ? `&collection=${encodeURIComponent(collection)}` : ""}`;
      const r = await fetch(url, { headers: { ..._authHeaders() } });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) {
        setItems(data.top_fiches || []);
        setTotal(data.total_impressions_30d || 0);
        setTrackingActive(!!data.tracking_active);
      } else if (r.status === 401 || r.status === 403) {
        toast.error("Accès refusé : connecte-toi en owner");
      } else {
        toast.error(data.error || `Erreur ${r.status}`);
      }
    } catch (_) { toast.error("Erreur réseau"); }
    finally { setLoading(false); }
  }, [collection]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="ops-impressions-page" style={pageStyle}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "#FBBF24" }}>📊 Top fiches vues par AYA</h1>
        <a href="/mon-compte" style={{ color: "#94A3B8", fontSize: 13, textDecoration: "underline" }}>← Mon Compte</a>
      </header>
      <p style={{ margin: "0 0 16px", color: "rgba(232,236,247,0.7)", fontSize: 13 }}>
        Leaderboard des fiches les plus citées par l'IA sur les 30 derniers jours.
      </p>

      {!trackingActive && (
        <div data-testid="impressions-tracking-disclaimer" style={disclaimerStyle}>
          ⚙️ <strong>Aucune impression encore enregistrée</strong> — Le tracking est actif depuis la v2.19.32. Pose une question dans le chat (ex: "trouve-moi une banque à Cocody") puis reviens ici.
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <select value={collection} onChange={(e) => setCollection(e.target.value)} data-testid="impressions-collection-filter" style={selectStyle}>
          {COLLECTIONS.map((c) => <option key={c.k} value={c.k}>{c.l}</option>)}
        </select>
        <div style={kpiStyle}>
          <span style={{ fontSize: 12, color: "#94A3B8" }}>Total impressions 30j :</span>
          <strong style={{ fontSize: 18, color: "#FBBF24", marginLeft: 6 }}>{total.toLocaleString("fr-FR")}</strong>
        </div>
        <button onClick={load} disabled={loading} data-testid="impressions-refresh" style={miniBtnStyle}>{loading ? "…" : "↻"}</button>
      </div>

      {loading && <div style={{ color: "#94A3B8", textAlign: "center", padding: 24 }}>Chargement…</div>}
      {!loading && items.length === 0 && (
        <div data-testid="impressions-empty" style={emptyStyle}>Aucune fiche dans cette catégorie.</div>
      )}
      {!loading && items.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(251,191,36,0.3)" }}>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Nom</th>
                <th style={thStyle}>Catégorie</th>
                <th style={thStyle}>Commune</th>
                <th style={{ ...thStyle, textAlign: "right" }}>30j</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Total</th>
                <th style={thStyle}>Dernière vue</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={`${it.collection}-${it.fiche_id}`} data-testid={`impression-row-${i}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{it.name}</td>
                  <td style={tdStyle}><span style={badgeStyle}>{it.collection}</span></td>
                  <td style={tdStyle}>{it.commune || "—"}</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: "#FBBF24", fontWeight: 700 }}>{it.impressions_30d}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{it.impressions_count}</td>
                  <td style={{ ...tdStyle, fontSize: 11, color: "#94A3B8" }}>
                    {it.last_impression_at ? new Date(it.last_impression_at).toLocaleDateString("fr-FR") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const pageStyle = { minHeight: "100vh", padding: "24px 18px", background: "radial-gradient(ellipse at top, #14223D 0%, #0A0F1F 70%)",
  color: "#E8ECF7", fontFamily: "system-ui, -apple-system, sans-serif", maxWidth: 1000, margin: "0 auto" };
const disclaimerStyle = { padding: "10px 14px", background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.35)",
  borderRadius: 10, marginBottom: 14, fontSize: 12.5, color: "#93C5FD" };
const selectStyle = { padding: "8px 12px", background: "rgba(15,23,42,0.55)", border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8, color: "#fff", fontSize: 13, fontFamily: "inherit", cursor: "pointer" };
const kpiStyle = { padding: "6px 12px", background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.3)",
  borderRadius: 999, display: "inline-flex", alignItems: "center" };
const miniBtnStyle = { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
  color: "#fff", width: 34, height: 34, borderRadius: 6, cursor: "pointer", fontSize: 14 };
const tableStyle = { width: "100%", borderCollapse: "collapse", background: "rgba(255,255,255,0.03)",
  borderRadius: 10, overflow: "hidden", fontSize: 13 };
const thStyle = { padding: "10px 12px", textAlign: "left", color: "#FBBF24", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 };
const tdStyle = { padding: "10px 12px", color: "#E8ECF7", verticalAlign: "middle" };
const badgeStyle = { fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(59,130,246,0.18)",
  color: "#93C5FD", border: "1px solid rgba(59,130,246,0.35)", fontWeight: 600 };
const emptyStyle = { padding: "30px 10px", textAlign: "center", color: "#94A3B8", fontSize: 13,
  background: "rgba(255,255,255,0.03)", borderRadius: 10 };
